import React from 'react';
import { TextareaProps } from '../../../types/DesignSystem/Textarea';
import FieldTitle from '../FieldTitle/FieldTitle';
import Typography from '../Typography/Typography';
import { FIELD_BOX, fieldBorder, FIELD_REST, FIELD_DISABLED } from '../fieldChrome';
import { FieldHelper, FieldMessages, fieldInvalid, hasMessage, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';

/**
 * Multi-line text field.
 *
 * The design system had `TextField` and no textarea, so every multi-line input in the
 * product is a raw `<textarea>` with its own classes — which is how you get eight
 * different border treatments for the same control.
 *
 * Deliberately mirrors `TextField`'s wiring rather than inventing its own: real label
 * association via `htmlFor` (not `aria-label`, which would give the field a second
 * name that can drift from the visible one — WCAG 2.5.3), `helperText` reachable
 * through `aria-describedby` so an errored field announces *why* (WCAG 3.3.1),
 * `aria-required` rather than native `required` so browser validation is not switched
 * on behind a caller's back, and the same border tiers from § 31.
 *
 * **`py-*` is correct here, unlike on every other field.** `TextField`, `Dropdown` and
 * `Search` had their vertical padding removed because they declare a fixed height from
 * the control grid, and padding *plus* a height is what made them 38px while `Button`
 * was 32 (see `controlSize.ts`). A textarea has no fixed height — it grows with its
 * rows — so padding is the only thing that insets the text. Do not "consistency-fix"
 * this by deleting it.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({
  label,
  value: valueProp,
  defaultValue,
  onChange,
  placeholder = '',
  rows = 4,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  maxLength,
  showCount = false,
  resize = 'vertical',
  className = '',
  labelSize,
  id,
  name,
  'data-testid': dataTestId,
  'aria-describedby': describedByProp,
  ...rest
}, ref) {
  const uid = React.useId();
  const fieldId = id ?? `${uid}-textarea`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  const countId = `${uid}-count`;

  const counting = showCount && maxLength !== undefined;
  /* § 356 / § 357 — see TextField. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState(valueProp, defaultValue, '');

  /* Composed, not replaced — rest-first spreading would otherwise silently drop a
     caller's own `aria-describedby`, which is the bug `TextField` records. */
  const describedBy = [
    describedByProp,
    ...messageIds({ helperId, errorId }, { helperText, errorMessage }),
    counting ? countId : undefined,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div className={className}>
      {label !== '' && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it stays the field's accessible name. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          size={labelSize}
          htmlFor={fieldId}
        />
      )}

      <textarea
        /* rest first — see the rule in Button.tsx. */
        {...rest}
        ref={ref}
        id={fieldId}
        name={name}
        rows={rows}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        onChange={(e) => { setValue(e.target.value); onChange?.(e.target.value, e); }}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        data-testid={dataTestId}
        /* Field chrome from the shared seam (`fieldChrome.ts`) — § 31 reversal:
           rest on `edge-strong`, hover whispers the ground, disabled recedes to
           `edge`. Numbers asserted in `borderTiers.test.tsx`. */
        className={`tw-w-full tw-min-h-20 tw-px-3 tw-py-2 ${FIELD_BOX} ${fieldBorder({ error: invalid, disabled })} tw-text-body-primary tw-text-ink placeholder:tw-text-ink-secondary
          ${resize === 'none' ? 'tw-resize-none' : 'tw-resize-y'}
          ${disabled ? FIELD_DISABLED : FIELD_REST}
          ${invalid ? 'focus:tw-ring-tone-danger-glyph' : ''}
        `}
      />

      {(helperText || hasMessage(errorMessage) || counting) && (
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mt-1">
          {hasMessage(errorMessage) ? (
            /* § 356 — two lines: a flex COLUMN, so each line keeps its own box (a plain
               wrapper would give the caption the row's line height). Without
               `errorMessage` the branch below renders the one line it always did. */
            <div className="tw-flex tw-flex-col tw-min-w-0">
              <FieldMessages
                helperId={helperId}
                errorId={errorId}
                helperText={helperText}
                errorMessage={errorMessage}
                error={error}
                data-testid={dataTestId}
              />
            </div>
          ) : helperText ? (
            /* The one helper seam (`fieldHelper.tsx`, § 302): alert while errored,
               remounted on the transition. The counter beside it stays a NON-live
               region on purpose (see its own comment). */
            <FieldHelper id={helperId} text={helperText} error={error} />
          ) : <span />}
          {counting && (
            /* Static text, NOT a live region. A counter that announced on every
               keystroke would make the field unusable with a screen reader; being in
               `aria-describedby` means it is read when the field takes focus, which is
               when the limit is worth knowing. */
            <Typography
              id={countId}
              variant="caption-metadata"
              className={`tw-shrink-0 tw-tabular-nums ${
                value.length >= (maxLength as number) ? 'tw-text-tone-danger-ink' : 'tw-text-ink-tertiary'
              }`}
              data-testid={dataTestId ? `${dataTestId}-count` : undefined}
            >
              {`${value.length} / ${maxLength}`}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
});

export default Textarea;
